import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import { DrawInspectionData } from "Pages/Admin/ProjectDetail/sections/DrawHistory/DrawHistory.types";
import { formatDate } from "utils/Common/Date";

export interface InspectionReportPdfProps {
  data: DrawInspectionData;
  drawNumber: number;
  projectName?: string;
}

// Same palette as the other Draw History PDFs (lifted from App.scss :root vars).
const COLORS = {
  b: "#14508c",
  bd: "#dde1e6",
  bg3: "#edf1f5",
  tx: "#111111",
  tx2: "#555c66",
  tx3: "#909399",
  green: "#1a6b35",
  amber: "#7a5c00",
  red: "#a32d2d",
};

const S = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 40,
    paddingHorizontal: 36,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: COLORS.tx,
    backgroundColor: "#ffffff",
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.bd,
  },
  headerTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 15,
    color: COLORS.b,
    marginBottom: 4,
  },
  headerSub: { fontSize: 8.5, color: COLORS.tx2 },
  headerRight: { alignItems: "flex-end" },
  headerMetaLine: { fontSize: 8, color: COLORS.tx2, marginBottom: 2 },

  inspSummaryRow: { flexDirection: "row", gap: 10, marginBottom: 16 },
  inspMetric: {
    flex: 1,
    alignItems: "center",
    backgroundColor: COLORS.bg3,
    borderRadius: 8,
    paddingVertical: 12,
  },
  inspMetricVal: { fontFamily: "Helvetica-Bold", fontSize: 17 },
  inspMetricValBlue: { color: COLORS.b },
  inspMetricValAmber: { color: COLORS.amber },
  inspMetricValGreen: { color: COLORS.green },
  inspMetricLabel: {
    fontSize: 7.5,
    color: COLORS.tx3,
    letterSpacing: 0.4,
    marginTop: 4,
  },

  card: {
    borderWidth: 0.75,
    borderColor: COLORS.bd,
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginBottom: 14,
    backgroundColor: "#ffffff",
  },
  cardTitleRow: {
    marginBottom: 11,
    paddingBottom: 9,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.bd,
  },
  cardTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: COLORS.tx,
    letterSpacing: 0.5,
  },
  narrativeText: {
    fontSize: 9,
    color: COLORS.tx,
    marginBottom: 6,
    lineHeight: 1.5,
  },
  narrativeFlag: { color: COLORS.red },
  emptyNote: { fontSize: 8, color: COLORS.tx3, fontStyle: "italic" },

  photoGrid: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  photoThumb: {
    width: 120,
    height: 90,
    borderRadius: 6,
    borderWidth: 0.75,
    borderColor: COLORS.bd,
    objectFit: "cover",
  },

  footer: {
    position: "absolute",
    bottom: 20,
    left: 36,
    right: 36,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: COLORS.bd,
    paddingTop: 6,
  },
  footerText: { fontSize: 7.5, color: COLORS.tx3 },
});

export default function InspectionReportPdf({
  data,
  drawNumber,
  projectName,
}: InspectionReportPdfProps) {
  const today = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const drawLabel = String(drawNumber).padStart(2, "0");
  const inspection = data.inspection;
  const flagCount = data.flagCount ?? 0;
  const photos = data.photos ?? [];

  return (
    <Document>
      <Page size="A4" style={S.page}>
        <View style={S.headerRow}>
          <View>
            <Text style={S.headerTitle}>CSC Field Inspection Report</Text>
            <Text style={S.headerSub}>
              Draw #{drawLabel}
              {inspection?.inspectionNumber != null
                ? ` · Inspection #${inspection.inspectionNumber}`
                : ""}
              {inspection?.inspectionDate
                ? ` · Conducted ${formatDate(inspection.inspectionDate, false)}`
                : ""}
              {inspection?.inspectorName
                ? ` by ${inspection.inspectorName}`
                : ""}
            </Text>
          </View>
          <View style={S.headerRight}>
            {projectName ? (
              <Text
                style={[
                  S.headerMetaLine,
                  { fontFamily: "Helvetica-Bold", color: COLORS.tx },
                ]}
              >
                {projectName}
              </Text>
            ) : null}
            <Text style={S.headerMetaLine}>Generated: {today}</Text>
          </View>
        </View>

        {!inspection ? (
          <Text style={S.emptyNote}>
            No CSC inspection report available for this draw.
          </Text>
        ) : (
          <>
            <View style={S.inspSummaryRow}>
              <View style={S.inspMetric}>
                <Text style={[S.inspMetricVal, S.inspMetricValBlue]}>
                  {inspection.cscEstimation ?? "—"}%
                </Text>
                <Text style={S.inspMetricLabel}>CSC ESTIMATE</Text>
              </View>
              <View style={S.inspMetric}>
                <Text style={[S.inspMetricVal, S.inspMetricValAmber]}>
                  {flagCount}
                </Text>
                <Text style={S.inspMetricLabel}>FLAGS RAISED</Text>
              </View>
              <View style={S.inspMetric}>
                <Text style={[S.inspMetricVal, S.inspMetricValGreen]}>
                  {photos.length}
                </Text>
                <Text style={S.inspMetricLabel}>PHOTOS CAPTURED</Text>
              </View>
            </View>

            <View style={S.card}>
              <View style={S.cardTitleRow}>
                <Text style={S.cardTitle}>INSPECTION SUMMARY</Text>
              </View>
              {inspection.note?.general && (
                <Text style={S.narrativeText}>{inspection.note.general}</Text>
              )}
              {inspection.note?.flags && (
                <Text style={[S.narrativeText, S.narrativeFlag]}>
                  Flags: {inspection.note.flags}
                </Text>
              )}
              {inspection.note?.offsite && (
                <Text style={S.narrativeText}>
                  Off-site materials: {inspection.note.offsite}
                </Text>
              )}
              {!inspection.note?.general &&
                !inspection.note?.flags &&
                !inspection.note?.offsite && (
                  <Text style={S.emptyNote}>
                    No inspection narrative recorded.
                  </Text>
                )}
            </View>

            <View style={S.card}>
              <View style={S.cardTitleRow}>
                <Text style={S.cardTitle}>
                  INSPECTION PHOTOS ({photos.length})
                </Text>
              </View>
              {photos.length === 0 ? (
                <Text style={S.emptyNote}>
                  No inspection photos captured.
                </Text>
              ) : (
                <View style={S.photoGrid}>
                  {photos.map((photo) =>
                    photo.url ? (
                      <Image
                        key={photo.id}
                        style={S.photoThumb}
                        src={photo.url}
                      />
                    ) : null,
                  )}
                </View>
              )}
            </View>
          </>
        )}

        <View style={S.footer} fixed>
          <Text style={S.footerText}>
            CSC New England · Field Inspection · Draw #{drawLabel} · {today}
          </Text>
          <Text
            style={S.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
